import React, { useCallback, useState, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { VelavuAsset } from "velavu-js-api";
import AssetCategorization from "../../data/asset-categorization";
import VelavuButton from "../../elements/velavu-button";
import CategorizationAutocomplete from "../../elements/velavu-categorization-autocomplete";
import VelavuInput from "../../elements/velavu-input";
import { useCategorizationSuggestions } from "../../helper/api-helper";
import TabAssetManageImage from "./tab-asset-manage-image";
import * as styles from "./tab-asset.module.scss";
import { VelavuAPI } from "velavu-js-api";
import { useAssetImageURLCache } from "../main/asset-image-url-cache-provider";
import { useHasPermission } from "../sign-in-context/sign-in-context";
import { useGlobalEmitter } from "../../emitter/emitter-context";
import { getBase64FromUrl } from "../../helper/image-helper";

function Notes(props: {
	value: string;
	onChange: Dispatch<SetStateAction<string>>;
	disabled: boolean;
}) {
	const { t } = useTranslation();
	const [height, setHeight] = useState(20);

	return (
		<>
			<span className={styles.label}>
				{t("asset_detail.asset.notes")}
			</span>
			<textarea
				className={styles.textarea}
				disabled={props.disabled}
				value={props.value}
				onChange={(e) => {
					const t = e.target as HTMLTextAreaElement;
					props.onChange(t.value);
					if (t.scrollHeight > t.clientHeight)
						setHeight(t.scrollHeight);
				}}
				style={{ height: height }}
			></textarea>
		</>
	);
}

export default function TabAsset(props: { asset: VelavuAsset }) {
	const { t } = useTranslation();
	const emitter = useGlobalEmitter();

	const canWriteAssets = useHasPermission("assets:write");

	const [originalName, setOriginalName] = useState(props.asset.name);
	const [originalCategory, setOriginalCategory] = useState(
		props.asset.category,
	);
	const [originalGroup, setOriginalGroup] = useState(props.asset.group);
	const [originalNotes, setOriginalNotes] = useState(props.asset.notes ?? "");

	const [assetName, setAssetName] = useState<string>(props.asset.name);
	const [assetCategorization, setAssetCategorization] = useState<
		AssetCategorization | undefined
	>({ category: props.asset.category, group: props.asset.group });
	const [assetNotes, setAssetNotes] = useState<string>(
		props.asset.notes ?? "",
	);

	const categorizationSuggestions = useCategorizationSuggestions();

	const [isSubmitting, setSubmitting] = useState(false);
	const fieldsDisabled = isSubmitting || !canWriteAssets;

	const saveChanges = useCallback(() => {
		setSubmitting(true);

		//Submitting the changes
		VelavuAPI.assets
			.modifySpecificAsset(props.asset.id, {
				name: assetName,
				category: assetCategorization!.category,
				group: assetCategorization!.group,
				notes: assetNotes,
			})
			.then((updatedAsset) => {
				setOriginalName(assetName);
				setOriginalCategory(assetCategorization!.category);
				setOriginalGroup(assetCategorization!.group);
				setOriginalNotes(assetNotes);

				emitter.emit("asset", {
					id: props.asset.id,
					data: updatedAsset,
				});
			})
			.catch((error) => console.log(error.response))
			.finally(() => setSubmitting(false));
	}, [assetName, assetCategorization, assetNotes, props.asset.id, emitter]);

	const deleteImage = useCallback(async () => {
		try {
			await VelavuAPI.assets.deleteProfilePicture(props.asset.id);
		} catch (error) {
			console.log(error);
			return;
		}

		emitter.emit("asset", {
			id: props.asset.id,
			data: {
				...props.asset,
				profile_img_url: undefined,
			},
		});
	}, [props.asset, emitter]);

	const uploadImage = useCallback(
		async (imageData: Blob) => {
			try {
				//Convert the image data to a base64 string
				const imageURL = URL.createObjectURL(imageData);
				let base64Data: string;
				try {
					base64Data = await getBase64FromUrl(imageURL);
				} finally {
					URL.revokeObjectURL(imageURL);
				}

				//Upload the image to the asset
				const updatedAsset =
					await VelavuAPI.assets.uploadProfilePicture(
						props.asset.id,
						base64Data,
					);
				emitter.emit("asset", {
					id: props.asset.id,
					data: updatedAsset,
				});
			} catch (error) {
				console.log(error);
			}
		},
		[props.asset, emitter],
	);

	const dataChanged =
		assetName !== originalName ||
		assetCategorization?.category !== originalCategory ||
		assetCategorization?.group !== originalGroup ||
		assetNotes !== originalNotes;

	const assetImageCache = useAssetImageURLCache();
	const assetImageURL = assetImageCache.getAssetImageURL(props.asset.id);

	return (
		<div className={styles.container}>
			<TabAssetManageImage
				style={{ marginBottom: 20 }}
				id={props.asset.id}
				category={props.asset.category}
				image={assetImageURL ?? undefined}
				canWrite={canWriteAssets}
				onDeleteImage={deleteImage}
				onUploadImage={uploadImage}
			/>
			<VelavuInput
				style={{ marginBottom: 12 }}
				type="text"
				titleText={t("asset_detail.asset.name")}
				placeholder={t("asset_detail.asset.name_placeholder")}
				value={assetName}
				onChange={setAssetName}
				disabled={fieldsDisabled}
				fullWidth
			/>
			<CategorizationAutocomplete
				containerProps={{ style: { marginBottom: 12 } }}
				value={assetCategorization}
				onChange={setAssetCategorization}
				categorization={categorizationSuggestions}
				disabled={fieldsDisabled}
			/>
			<Notes
				value={assetNotes}
				onChange={setAssetNotes}
				disabled={fieldsDisabled}
			/>

			{canWriteAssets && (
				<VelavuButton
					className={styles.button}
					label={t("common.save_changes")}
					disabled={
						isSubmitting ||
						!(
							assetName.trim().length > 0 &&
							assetCategorization &&
							dataChanged
						)
					}
					onClick={saveChanges}
				/>
			)}
		</div>
	);
}
